import { useEffect, useMemo, useState } from "react";
import { Button, Col, Row } from "reactstrap";
import { DEFAULT_OPTION } from "../../../../utils/Constant";
import SelectBox from "../../../../components/SelectBox";
import { getDistrictsDataAPICall } from "../../../../api/chmActivityAPI";
import { useDispatch, useSelector } from "react-redux";
import { FormUtils } from "../../../../utils/FormUtils";
import SearchBox from "../../../../components/SearchBox";
import { VendorEnterprisePayload } from "../../../../models/VendorModels";
import {
  blocksByDistrictDataAPI,
  districtsByStateDataAPI,
  icvGetAllVillagesDataAPICall,
  icvGetDistrictsDataAPICall,
  icvGetMandalDataAPICall,
  icvGetStatesDataAPICall,
  icvGetVendorDetailsByID,
  SurveyorCreationAPI,
  updateVendorAPI,
  vendorCreationAPI,
} from "../../../../api/icvActivityAPI";
import { Controller, useForm } from "react-hook-form";
import FileUpload from "../../../../components/FileUpload";
import FileUploadField from "../../../../components/FileUploadField";
import { Tooltip } from "@mui/material";
import ModalComponent from "../../../../components/ModalComponent";
import { SurveyorCreation } from "./SurveyorCreation";
import MultiSelectDropdown from "../../../../components/MultiSelectDropdown";
import {
  LoggedUserType,
  SelectOptionType,
} from "../../../../models/GlobalModels";
import { loginSelector } from "../../../../redux/slices/loginSlice";
import { CircleSpinnerOverlay } from "react-spinner-overlay";
import { fileValidationRules } from "../../../../utils/Util";
import CommonFields from "./CommonFields";

interface VendorCreationProps {
  setIsFormValid?: (isValid: boolean) => void;
  setSubmitHandler?: (handler: () => void) => void;
  setOpenModal?: (openModal: boolean) => void;
  mode: string;
  vendorCode?: string;
}

export const VendorCreation = ({
  setIsFormValid,
  setSubmitHandler,
  setOpenModal,
  mode,
  vendorCode,
}: VendorCreationProps) => {
  const getStatusLabel = (status: boolean) => {
    if (status) {
      return { label: "Active", value: true };
    } else {
      return { label: "Inactive", value: false };
    }
  };

  const {
    control,
    handleSubmit,
    watch,
    setValue,
    getValues,
    reset,
    formState: { errors, isValid },
  } = useForm<VendorEnterprisePayload>({
    defaultValues: {
      vendor: {
        vendorType: { label: "Vendor Enterprise", value: "VENDOR_ENTERPRISE" },
        vendorName: "",
        mobileNumber: "",
        email: "",
        stateId: DEFAULT_OPTION,
        districtId: DEFAULT_OPTION,
        isActive: { label: "Active", value: true },
        address: "",
        pan: {
          preview: "",
          file: null,
          type: "",
        },
        aadhaar: {
          preview: "",
          file: null,
          type: "",
        },
        bankPassbook: {
          preview: "",
          file: null,
          type: "",
        },
        educationCertificate: {
          preview: "",
          file: null,
          type: "",
        },
        engagementLetter: {
          preview: "",
          file: null,
          type: "",
        },
        passportSizePhoto: {
          preview: "",
          file: null,
          type: "",
        },
      },
      vendorAllocation: [
        {
          stateId: [],
          districtId: [],
          blockId: [],
        },
      ],
      surveyorType: { label: "Vendor Individual", value: "VENDOR_INDIVIDUAL" },
      firstName: "",
      lastName: "",
      fatherOrRelativeName: "",
      emailId: "",
      mobileNumber: "",
      address: "",
      stateId: DEFAULT_OPTION,
      districtId: DEFAULT_OPTION,
      tehsilBlockId: DEFAULT_OPTION,
      village: DEFAULT_OPTION,
      isActive: { label: "Active", value: true },
      panNumber: "",
      aadhaarNumber: "",
      bankName: "",
      branchName: "",
      bankAccountNumber: "",
      ifscCode: "",
      highestQualification: DEFAULT_OPTION,
      deviceId: "",
      panCardBase64: {
        file: null,
        preview: "",
        type: "",
      },
      aadhaarCardBase64: {
        file: null,
        preview: "",
        type: "",
      },
      bankPassbookBase64: {
        file: null,
        preview: "",
        type: "",
      },
      engagementLetterBase64: {
        file: null,
        preview: "",
        type: "",
      },
      passportPhotoBase64: {
        file: null,
        preview: "",
        type: "",
      },
      educationCertificateBase64: {
        file: null,
        preview: "",
        type: "",
      },
      allocations: [
        {
          stateId: [],
          districtId: [],
          tehsilBlockId: [],
          villageId: [],
        },
      ],
    },
    mode: "all",
  });

  const dispatch: any = useDispatch();

  const {
    storeICVGetStatesData,
    storeICVGetDistrictData,
    storeICVDistrictsByStateData,
    storeICVBlocksByDistricData,
    storeICVVendorDetailsById,
    isCrudLoading,
    closeModal,
  } = useSelector((state: any) => state.icvActivity);

  const vendorType = watch("vendor.vendorType");

  const individualVendorData =
    storeICVVendorDetailsById?.data && storeICVVendorDetailsById?.data[0];

  const editFieldStateOptions = individualVendorData?.allocations.map(
    (item: any) => ({
      label: item?.stateName,
      value: item?.stateId,
    }),
  );

  const editFieldDistrictOptions = individualVendorData?.allocations.map(
    (item: any) => ({
      label: item.districtName,
      value: item.districtId,
      stateId: item.stateId,
      stateName: item.stateName,
    }),
  );

  const editFieldTehsilOptions = individualVendorData?.allocations
    ?.filter((item: any) => item?.blockId)
    .map((item: any) => ({
      label: item.blockName,
      value: item.blockId,
      stateId: item.stateId,
      stateName: item.stateName,
      districtId: item.districtId,
      districtName: item.districtName,
    }));

  const user: LoggedUserType = useSelector(loginSelector).user;
  const selectedState = watch("vendor.stateId");

  const vendorAllocationState = watch("vendorAllocation.0.stateId");
  const vendorAllocationDistrict = watch("vendorAllocation.0.districtId");
  const vendorAllocationBlock = watch("vendorAllocation.0.blockId");

  const [openSurveyorModal, setOpenSurveyorModal] = useState(false);

  const toggleOpenSurveyorModal = () => {
    setOpenSurveyorModal((prev) => !prev);
  };

  const uploadFields = [
    {
      name: "vendor.pan",
      label: "PAN Card",
      imageTitle: "PAN Card",
      rules: {
        required: "PAN is required",
        validate: fileValidationRules.validate,
      },
    },
    {
      name: "vendor.aadhaar",
      label: "Aadhaar Card",
      imageTitle: "Aadhaar Card",
      rules: {
        required: "Aadhaar Card is required",
        validate: fileValidationRules.validate,
      },
    },
    {
      name: "vendor.bankPassbook",
      label: "Bank Passbook",
      imageTitle: "Bank Passbook",
      rules: {
        required: "Bank Passbook is required",
        validate: fileValidationRules.validate,
      },
    },
    {
      name: "vendor.educationCertificate",
      label: "Education Certificate",
      imageTitle: "Education Certificate",
      rules: {
        required: "Education Certificate is required",
        validate: fileValidationRules.validate,
      },
    },
    {
      name: "vendor.engagementLetter",
      label: "Engagement Letter",
      imageTitle: "Engagement Letter",
      rules: {
        required: "Engagement Letter is required",
        validate: fileValidationRules.validate,
      },
    },
    {
      name: "vendor.passportSizePhoto",
      label: "Passport Size Photograph",
      imageTitle: "Passport Size Photograph",
      rules: {
        required: "Passport Size Photograph is required",
        validate: fileValidationRules.validate,
      },
    },
  ];

  const statusDropdownOptions = [
    { label: "Active", value: true },
    { label: "Inactive", value: false },
  ];

  const statesData = FormUtils.convertDataList(
    storeICVGetStatesData?.data ?? [],
  );

  const districtData = FormUtils.convertDataList(
    storeICVGetDistrictData?.data ?? [],
  );

  // Multi States, Districts and village - Vendor Allocation
  const multiDistrictsData = FormUtils.convertMultiDataDistrictList(
    storeICVDistrictsByStateData?.data ?? [],
  );

  const multiBlocksData = FormUtils.convertMultiBlocksList(
    storeICVBlocksByDistricData?.data ?? [],
  );

  // Effects
  useEffect(() => {
    // Fetching the states initial data on component mount
    dispatch(icvGetStatesDataAPICall());
  }, [dispatch]);

  useEffect(() => {
    if (vendorCode) {
      dispatch(icvGetVendorDetailsByID(vendorCode));
    }
  }, []);

  // Restricting the Districts  when state is removed from the state dropdown
  useEffect(() => {
    if (!multiDistrictsData?.length) return;

    const filteredDistricts: any = vendorAllocationDistrict.filter(
      (district: any) => {
        return vendorAllocationState.some(
          (state: any) => state.value === district.stateId,
        );
      },
    );

    setValue("vendorAllocation.0.districtId", filteredDistricts);
  }, [vendorAllocationState]);

  // Restricting the Tehsil/Blocks when district is removed from district dropdown
  useEffect(() => {
    if (!multiBlocksData?.length) return;

    const filteredBlocks: any = vendorAllocationBlock.filter((block: any) => {
      return vendorAllocationDistrict.some(
        (district: any) => district.value === block.districtId,
      );
    });

    setValue("vendorAllocation.0.blockId", filteredBlocks);
  }, [vendorAllocationDistrict]);

  useEffect(() => {
    setIsFormValid && setIsFormValid(isValid);
  }, [isValid]);

  useEffect(() => {
    if (closeModal) {
      setOpenModal?.(false);
    }
  }, [closeModal]);

  useEffect(() => {
    setSubmitHandler && setSubmitHandler(() => handleSubmit(onSubmit));
  }, [handleSubmit]);

  // To get the districts of selected states in edit mode
  useEffect(() => {
    const stateCodes: string[] = Array.from(
      new Set(
        editFieldStateOptions?.map((item: SelectOptionType) => item.value),
      ),
    );

    if (mode === "edit") {
      dispatch(districtsByStateDataAPI(stateCodes));
    }
  }, [individualVendorData]);

  // To get the blocks of selected districts in edit mode
  useEffect(() => {
    const stateCodes: string[] = Array.from(
      new Set(editFieldDistrictOptions?.map((item: any) => item.stateId)),
    );

    const districtCodes: string[] = Array.from(
      new Set(editFieldDistrictOptions?.map((item: any) => item.value)),
    );

    if (mode === "edit") {
      dispatch(blocksByDistrictDataAPI(stateCodes, districtCodes));
    }
  }, [individualVendorData]);

  useEffect(() => {
    if (mode === "edit" && individualVendorData) {
      const {
        vendorName,
        mobileNumber,
        email,
        stateName,
        stateId,
        districtName,
        districtId,
        isActive,
        address,
        panCard,
        panCardType,
        aadhaarCard,
        aadhaarCardType,
        bankPassbook,
        bankPassbookType,
        educationCertificate,
        educationCertificateType,
        engagementLetter,
        engagementLetterType,
        passportPhoto,
        passportPhotoType,
        vendorType,
      } = individualVendorData;
      reset({
        vendor: {
          vendorType:
            vendorType === "VENDOR_ENTERPRISE"
              ? {
                  label: "Vendor Enterprise",
                  value: "VENDOR_ENTERPRISE",
                }
              : {
                  label: "Vendor Individual",
                  value: "VENDOR_INDIVIDUAL",
                },
          vendorName: vendorName || "",
          mobileNumber: mobileNumber || "",
          email: email || "",
          stateId: {
            label: stateName,
            value: stateId,
          },
          districtId: {
            label: districtName,
            value: districtId,
          },
          isActive: isActive
            ? getStatusLabel(individualVendorData.isActive)
            : { label: "Active", value: true },
          address: address || "",
          pan: {
            preview: panCard || "",
            type: panCardType || "",
          },
          aadhaar: {
            preview: aadhaarCard || "",
            type: aadhaarCardType || "",
          },
          bankPassbook: {
            preview: bankPassbook || "",
            type: bankPassbookType || "",
          },
          educationCertificate: {
            preview: educationCertificate || "",
            type: educationCertificateType || "",
          },
          engagementLetter: {
            preview: engagementLetter || "",
            type: engagementLetterType || "",
          },
          passportSizePhoto: {
            preview: passportPhoto || "",
            type: passportPhotoType || "",
          },
        },
        vendorAllocation: [
          {
            stateId: editFieldStateOptions,
            districtId: editFieldDistrictOptions,
            blockId: editFieldTehsilOptions || [],
          },
        ],
      });
    }
  }, [individualVendorData]);

  // Handlers
  const onchangeDropdown = (
    label: string,
    event: any,
    fromAllocatedDetails?: boolean,
  ) => {
    switch (label) {
      case "State":
        if (!fromAllocatedDetails) {
          dispatch(icvGetDistrictsDataAPICall(event?.value));
        } else {
          const stateCodes: string[] = Array.from(
            new Set(event.map((item: SelectOptionType) => item.value)),
          );
          dispatch(districtsByStateDataAPI(stateCodes));
        }
        break;
      case "District":
        if (!fromAllocatedDetails) {
          if (typeof selectedState?.value === "string") {
            dispatch(
              icvGetMandalDataAPICall(selectedState?.value, event?.value),
            );
          }
        } else {
          const stateCodes: string[] = Array.from(
            new Set(event.map((item: any) => item.stateId)),
          );

          const districtCodes: string[] = Array.from(
            new Set(event.map((item: any) => item.value)),
          );

          dispatch(blocksByDistrictDataAPI(stateCodes, districtCodes));
        }

        break;
      case "block":
        if (!fromAllocatedDetails) {
          if (
            typeof vendorAllocationState[0]?.value === "string" &&
            typeof vendorAllocationDistrict[0]?.value === "string"
          ) {
            dispatch(
              icvGetAllVillagesDataAPICall(
                vendorAllocationState[0]?.value,
                vendorAllocationDistrict[0]?.value,
                event?.value,
              ),
            );
          }
        } else {
          if (
            typeof vendorAllocationState[0]?.value === "string" &&
            typeof vendorAllocationDistrict[0]?.value === "string"
          ) {
            dispatch(
              icvGetAllVillagesDataAPICall(
                vendorAllocationState[0]?.value,
                vendorAllocationDistrict[0]?.value,
                event?.value,
              ),
            );
          }
        }

        break;
      default:
        dispatch(getDistrictsDataAPICall(event?.value));
        break;
    }
  };

  const onSubmit = (data: VendorEnterprisePayload) => {
    mode === "edit"
      ? dispatch(updateVendorAPI(data, user?.emailId, vendorCode))
      : getValues("vendor.vendorType").value === "VENDOR_ENTERPRISE"
        ? dispatch(vendorCreationAPI(data, user.emailId))
        : dispatch(SurveyorCreationAPI(data, user.emailId, true));
  };

  return (
    <div className="filter-section mb-2">
      {isCrudLoading && (
        <CircleSpinnerOverlay
          loading={isCrudLoading}
          overlayColor="rgba(147, 166, 199, 0.1)"
          color="#5C79AA"
          zIndex={9999}
        />
      )}
      {openSurveyorModal && (
        <ModalComponent
          openModal={openSurveyorModal}
          toggleModal={toggleOpenSurveyorModal}
          title={"Surveyor Master Creation"}
          children={<SurveyorCreation />}
          needXlSize
          footer={
            <div className="d-flex justify-content-end">
              <Button
                variant="outline-primary"
                className="mx-2 cl-btn-outline-primary"
                onClick={() => {
                  setOpenSurveyorModal(false);
                }}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                className="mx-2 cl-btn-primary"
                // disabled={!assignFormValid}
                // onClick={handleAssignSubmit}
              >
                Submit
              </Button>
            </div>
          }
        />
      )}
      {/* Personal Information Section */}

      {vendorType.value === "VENDOR_ENTERPRISE" ? (
        <>
          <Row>
            {/* Vendor Type */}
            <Col xs={12} md={4} lg={3}>
              <Controller
                name="vendor.vendorType"
                control={control}
                rules={{ required: "Vendor type is required" }}
                render={({ field, fieldState: { error } }) => (
                  <SelectBox
                    required
                    {...field}
                    disabled={mode === "edit"}
                    label="Vendor Type"
                    options={[
                      {
                        label: "Vendor Enterprise",
                        value: "VENDOR_ENTERPRISE",
                      },
                      {
                        label: "Vendor Individual",
                        value: "VENDOR_INDIVIDUAL",
                      },
                    ]}
                    className="css-select-bg"
                  />
                )}
              />
            </Col>

            {/* Vendor Name*/}
            <Col xs={12} md={4} lg={3}>
              <Controller
                name="vendor.vendorName"
                control={control}
                rules={{ required: "Vendor name is required" }}
                render={({ field, fieldState: { error } }) => (
                  <div style={{ position: "relative" }}>
                    <SearchBox
                      required
                      {...field}
                      label="Vendor Name"
                      placeHolder="Enter Vendor Name"
                      error={!!error}
                      errorMessage={error?.message}
                      disabled={mode === "edit"}
                    />
                  </div>
                )}
              />
            </Col>

            {/* Vendor Email */}
            {
              <Col xs={12} md={4} lg={3}>
                <Controller
                  name="vendor.email"
                  control={control}
                  rules={{
                    required: "Email is required",
                    pattern: {
                      value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                      message: "Enter a valid email address",
                    },
                  }}
                  render={({ field, fieldState: { error } }) => (
                    <div style={{ position: "relative" }}>
                      <SearchBox
                        {...field}
                        label="Email"
                        placeHolder="Enter Email"
                        required
                        error={!!error}
                        errorMessage={error?.message}
                      />
                    </div>
                  )}
                />
              </Col>
            }
            {/* Mobile Number */}
            <Col xs={12} md={4} lg={3}>
              <Controller
                name="vendor.mobileNumber"
                control={control}
                rules={{
                  required: "mobile number is required",
                  pattern: {
                    value: /^[6-9][0-9]{9}$/,
                    message:
                      "Enter a valid 10‑digit mobile number starting with 6–9",
                  },
                  minLength: {
                    value: 10,
                    message: "mobile number must be 10 digits",
                  },
                  maxLength: {
                    value: 10,
                    message: "mobile number must be 10 digits",
                  },
                }}
                render={({ field, fieldState: { error } }) => (
                  <div style={{ position: "relative" }}>
                    <SearchBox
                      required
                      {...field}
                      label="Mobile Number"
                      placeHolder="Enter Mobile Number"
                      disabled={mode === "edit"}
                      error={!!error}
                      maxLength={10}
                      errorMessage={error?.message}
                      onChange={(e: any) => {
                        const value = e.target.value
                          .replace(/\D/g, "")
                          .slice(0, 10);

                        field.onChange(value);
                      }}
                    />
                  </div>
                )}
              />
            </Col>

            {/* Address*/}
            <Col xs={12} md={4} lg={3}>
              <Controller
                name="vendor.address"
                control={control}
                rules={{ required: "Address is required" }}
                render={({ field, fieldState: { error } }) => (
                  <div style={{ position: "relative" }}>
                    <SearchBox
                      required
                      {...field}
                      label="Address"
                      placeHolder="Enter Address"
                      error={!!error}
                      errorMessage={error?.message}
                    />
                  </div>
                )}
              />
            </Col>

            {/* Status Dropdown */}
            <Col xs={12} md={4} lg={3}>
              <Controller
                name="vendor.isActive"
                control={control}
                rules={{ required: "Status is required" }}
                render={({ field, fieldState: { error } }) => (
                  <SelectBox
                    required
                    {...field}
                    label="Status"
                    options={statusDropdownOptions}
                    className="css-select-bg"
                  />
                )}
              />
            </Col>

            {/* State Dropdown */}
            <Col xs={12} md={4} lg={3}>
              <Controller
                name="vendor.stateId"
                control={control}
                rules={{
                  validate: (val) => (val?.value ? true : "State is required"),
                }}
                render={({ field, fieldState: { error } }) => (
                  <SelectBox
                    required
                    {...field}
                    label="State"
                    disabled={mode === "edit"}
                    options={statesData}
                    className="css-select-bg"
                    onChange={(event: any) => {
                      if (event != null) {
                        field.onChange(event);
                        setValue("vendor.districtId", DEFAULT_OPTION);

                        onchangeDropdown("State", event);
                      } else {
                        field.onChange(DEFAULT_OPTION);
                        setValue("vendor.districtId", DEFAULT_OPTION);
                      }
                    }}
                  />
                )}
              />
            </Col>

            {/* District Dropdown */}
            <Col xs={12} md={4} lg={3}>
              <Controller
                name="vendor.districtId"
                control={control}
                rules={{
                  validate: (val) =>
                    val?.value ? true : "District is required",
                }}
                render={({ field, fieldState: { error } }) => (
                  <SelectBox
                    required
                    {...field}
                    disabled={
                      selectedState?.label === "Select" || mode === "edit"
                    }
                    label="District"
                    options={districtData}
                    onChange={(event: any) => {
                      if (event != null) {
                        setValue("vendor.districtId", event, {
                          shouldValidate: true,
                        });
                        onchangeDropdown("District", event);
                      } else {
                        field.onChange(DEFAULT_OPTION);
                      }
                    }}
                  />
                )}
              />
            </Col>
          </Row>
          <Row>
            <Col className="mt-2" style={{ marginBottom: "-8px" }} xs={12}>
              <div className="upload-note-container mb-3">
                <h6 className="upload-note-title">Note:</h6>

                <ul className="upload-note-list mb-0">
                  <li>
                    Supported file types: JPG, JPEG, PNG, WEBP, PDF
                  </li>
                  <li>Maximum file size: 5MB</li>
                </ul>
              </div>
            </Col>
          </Row>
          {/* Documents Section */}
          <Row>
            {uploadFields.map((field) => (
              <FileUploadField
                key={field.name}
                name={field.name}
                control={control}
                label={field.label}
                imageTitle={field.imageTitle}
                rules={field.rules}
                required={field.rules?.required ? true : false}
                disabled={mode === "edit"}
              />
            ))}
          </Row>
          {/* Allowcation Details Section */}
          <Row>
            <h1 className="sub-heading mt-2">Allocation Details</h1>

            {/* Allocation Multi State Dropdown */}
            <Col xs={12} md={4} lg={3}>
              <Controller
                name="vendorAllocation.0.stateId"
                control={control}
                rules={{ required: "State is required" }}
                render={({ field, fieldState: { error } }) => (
                  <MultiSelectDropdown
                    {...field}
                    optional
                    fromVendorCreation
                    label="State"
                    options={statesData}
                    onChange={(event: any) => {
                      if (event != null) {
                        setValue("vendorAllocation.0.stateId", event, {
                          shouldValidate: true,
                        });

                        onchangeDropdown("State", event, true);
                      } else {
                        setValue("vendorAllocation.0.stateId", [
                          DEFAULT_OPTION,
                        ]);
                      }
                    }}
                  />
                )}
              />
            </Col>

            {/*Allocation Multi Select District Dropdown */}
            <Col xs={12} md={4} lg={3}>
              <Controller
                name="vendorAllocation.0.districtId"
                control={control}
                rules={{ required: "District is required" }}
                render={({ field, fieldState: { error } }) => (
                  <MultiSelectDropdown
                    {...field}
                    optional
                    fromVendorCreation
                    disabled={Number(vendorAllocationState.length) === 0}
                    label="District"
                    options={multiDistrictsData}
                    onChange={(event: any) => {
                      if (event != null) {
                        setValue("vendorAllocation.0.districtId", event, {
                          shouldValidate: true,
                        });
                        onchangeDropdown("District", event, true);
                      } else {
                        setValue("vendorAllocation.0.districtId", [
                          DEFAULT_OPTION,
                        ]);
                      }
                    }}
                  />
                )}
              />
            </Col>

            {/* Allocation Multi Selection Mandal Dropdown */}
            <Col xs={12} md={4} lg={3}>
              <Controller
                name="vendorAllocation.0.blockId"
                control={control}
                render={({ field, fieldState: { error } }) => (
                  <MultiSelectDropdown
                    {...field}
                    fromVendorCreation
                    disabled={Number(vendorAllocationDistrict.length) === 0}
                    label="Tehsil/Block"
                    options={multiBlocksData}
                    onChange={(event: any) => {
                      if (event != null) {
                        setValue("vendorAllocation.0.blockId", event, {
                          shouldValidate: true,
                        });
                      } else {
                        setValue("vendorAllocation.0.blockId", [
                          DEFAULT_OPTION,
                        ]);
                      }
                    }}
                  />
                )}
              />
            </Col>
          </Row>
        </>
      ) : (
        <Row>
          <Col xs={12} md={4} lg={3}>
            <Controller
              name="vendor.vendorType"
              control={control}
              rules={{ required: "Vendor type is required" }}
              render={({ field, fieldState: { error } }) => (
                <SelectBox
                  required
                  {...field}
                  label="Vendor Type"
                  disabled={mode === "edit"}
                  options={[
                    { label: "Vendor Enterprise", value: "VENDOR_ENTERPRISE" },
                    { label: "Vendor Individual", value: "VENDOR_INDIVIDUAL" },
                  ]}
                  className="css-select-bg"
                />
              )}
            />
          </Col>
          <CommonFields
            control={control}
            mode={mode}
            setValue={setValue}
            surveyorType={{
              label: "Vendor Individual",
              value: "VENDOR_INDIVIDUAL",
            }}
            watch={watch}
            setOpenModal={setOpenModal}
          />
        </Row>
      )}
    </div>
  );
};
